import { useNavigate } from 'react-router-dom'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'

interface Notification {
  id: string
  type: string
  title: string
  body: string
  read: number
  created_at: string
  metadata?: string
  organization_id?: string
}

interface NotificationPanelProps {
  notifications: Notification[]
  loading: boolean
  onMarkAsRead: (id: string) => void
  onMarkAllAsRead: () => void
  onClose: () => void
}

const typeIcons: Record<string, string> = {
  approval_queue: '✓',
  budget_alert: '💰',
  vat_deadline: '📅',
  invoice_overdue: '⚠️',
  invoice_paid: '💵',
  member_payment: '👤',
  member_joined: '👥',
  transaction_created: '📝',
  transaction_verified: '✅',
  low_balance: '⚡',
  system: '🔔',
}

const typeColors: Record<string, string> = {
  approval_queue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  budget_alert: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  vat_deadline: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  invoice_overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  invoice_paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  member_payment: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  member_joined: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  transaction_created: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  transaction_verified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  low_balance: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  system: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Juuri nyt'
  if (diffMins < 60) return `${diffMins} min sitten`
  if (diffHours < 24) return `${diffHours} t sitten`
  if (diffDays < 7) return `${diffDays} pv sitten`
  
  return date.toLocaleDateString('fi-FI')
}

function getNotificationLink(notification: Notification): string | null {
  if (!notification.metadata) return null
  
  try {
    const metadata = JSON.parse(notification.metadata)
    
    if (metadata.approvalQueueId) return `/approval-queue`
    if (metadata.transactionId) return `/transactions?id=${metadata.transactionId}`
    if (metadata.invoiceId) return `/invoices/${metadata.invoiceId}`
    if (metadata.memberId) return `/members/${metadata.memberId}`
    if (metadata.vatReturnId) return `/vat-returns/${metadata.vatReturnId}`
    if (metadata.budgetId) return `/category-budget-limits`
    if (metadata.url) return metadata.url
    
    return null
  } catch {
    return null
  }
}

export default function NotificationPanel({
  notifications,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
}: NotificationPanelProps) {
  const navigate = useNavigate()
  const unreadCount = notifications.filter(n => !n.read).length

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkAsRead(notification.id)
    }
    
    const link = getNotificationLink(notification)
    if (link) {
      navigate(link)
      onClose()
    }
  }

  return (
    <div
      className="
        absolute right-0 top-full mt-2 w-96
        bg-white dark:bg-slate-800
        rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700
        z-50 overflow-hidden
      "
      role="dialog"
      aria-label="Ilmoitukset"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            Ilmoitukset
          </h3>
          {unreadCount > 0 && (
            <Badge variant="error" size="sm">
              {unreadCount}
            </Badge>
          )}
        </div>
        
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllAsRead}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Merkitse kaikki luetuksi
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 px-4">
            <div className="text-4xl mb-2">🔔</div>
            <p className="text-slate-500 dark:text-slate-400">
              Ei ilmoituksia
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
              Uudet ilmoitukset näkyvät täällä
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {notifications.map((notification) => {
              const icon = typeIcons[notification.type] || '🔔'
              const colorClass = typeColors[notification.type] || typeColors.system
              const link = getNotificationLink(notification)
              
              return (
                <div
                  key={notification.id}
                  onClick={() => link && handleNotificationClick(notification)}
                  className={`
                    flex gap-3 p-4 transition-colors
                    ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}
                    ${link ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50' : ''}
                  `}
                >
                  {/* Icon */}
                  <div className={`
                    flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg
                    ${colorClass}
                  `}>
                    {icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className={`
                        text-sm font-medium truncate
                        ${!notification.read 
                          ? 'text-slate-900 dark:text-white' 
                          : 'text-slate-700 dark:text-slate-300'
                        }
                      `}>
                        {notification.title}
                      </h4>
                      
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onMarkAsRead(notification.id)
                          }}
                          className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                          title="Merkitse luetuksi"
                        >
                          ✓
                        </button>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">
                      {notification.body}
                    </p>
                    
                    <span className="text-xs text-slate-400 dark:text-slate-500 mt-1 block">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                  </div>

                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => {
            navigate('/settings/notifications')
            onClose()
          }}
        >
          Ilmoitusasetukset →
        </Button>
      </div>
    </div>
  )
}
